/**
 * The presence-tracking opt-in (geofencing plan Q4), as this app knows it.
 *
 * The flag is the hub user's own (`User.presenceTrackingConsent`). It reaches
 * this module two ways, and both are honest reads of the hub:
 *
 *  - `readPresenceConsent()` — the reader, `GET /users/me`, re-read before each
 *    cloud report (the tracker calls it);
 *  - `rememberPresenceConsent()` — the writer's own answer, the value the hub
 *    returned from `PUT /presence/consent`
 *    (`views/Settings/PresenceSettings.vue`). Measured 2026-10-02: the hub's
 *    profile payload does **not** carry the field yet (`USER_PROFILE_SELECT`,
 *    `otto/src/auth/index.ts`), so without this the app could never learn an
 *    opt-in and the cloud report would stay closed for a person who opted in
 *    from another client; a hub that serves the field is picked up by the read
 *    above with no code change (a follow-up card asks the hub lane for it).
 *
 * Fail closed: `null` (nothing known yet) is NOT consent, and an answer that
 * does not carry the field at all is NOT an answer — it must never clear an
 * opt-in this app recorded from the hub's own write path. A fix must never
 * leave the device because the app has not been told yes.
 *
 * Module state rather than a store: the reader (`composables/useLocationTracking.ts`)
 * runs on an interval, outside component setup, and nothing here needs pinia.
 */
import { ref } from 'vue';

/** The last answer the hub gave: true, false, or null while never read. */
export const presenceConsent = ref<boolean | null>(null);

/** Remember an answer — for a caller that already has one in hand. */
export function rememberPresenceConsent(value: boolean | null): void {
  presenceConsent.value = value;
}

/** The gate: true only for a positively known opt-in. */
export function presenceConsentGranted(): boolean {
  return presenceConsent.value === true;
}

/**
 * What a profile payload *says* about the opt-in: `true`/`false` when the
 * payload carries a boolean, `null` when it carries nothing usable. `null` is
 * "no answer", not "no consent" — the caller decides what to do with it (this
 * module keeps what it already knows, and stays closed when it knows nothing).
 */
export function consentFromProfile(payload: { presenceTrackingConsent?: unknown } | null | undefined): boolean | null {
  return typeof payload?.presenceTrackingConsent === 'boolean' ? payload.presenceTrackingConsent : null;
}

/**
 * Ask the hub for the opt-in, remember the answer, and answer with the gate as
 * it now stands. Never throws, and an unreadable answer leaves the last known
 * value in place: a hub that cannot be reached must not flip an opt-in that was
 * already given, and an opt-in that was never read stays closed.
 *
 * `./api` is imported lazily so this module (and its gate) can be exercised by
 * a Node test — the API layer reaches stores that only load inside the app.
 */
export async function readPresenceConsent(): Promise<boolean> {
  try {
    const { apiFetch } = await import('./api');
    const me = await apiFetch<{ presenceTrackingConsent?: unknown }>('/users/me');
    const answer = consentFromProfile(me);
    if (answer !== null) rememberPresenceConsent(answer);
  } catch (error) {
    console.warn('[PresenceConsent] Could not read the tracking opt-in from the hub:', error);
  }
  return presenceConsentGranted();
}
